import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { ReceivingSummary } from '../../types';

interface ReceivingSummaryCardProps {
  summary?: ReceivingSummary;
  isLoading?: boolean;
}

export function ReceivingSummaryCard({ summary, isLoading }: ReceivingSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Receiving Overview</CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/procurement">View All</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">
              {summary.draft_orders}
            </div>
            <div className="text-sm text-muted-foreground">Draft</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {summary.in_progress_orders}
            </div>
            <div className="text-sm text-muted-foreground">In Progress</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {summary.pending_qc_orders}
            </div>
            <div className="text-sm text-muted-foreground">Pending QC</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {summary.completed_orders}
            </div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
        </div>

        {summary.expected_today > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-sm font-medium text-blue-800">
              {summary.expected_today} order{summary.expected_today > 1 ? 's' : ''} expected today
            </div>
          </div>
        )}

        {summary.recent_orders && summary.recent_orders.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">Recent Activity</div>
            <div className="space-y-2">
              {summary.recent_orders.slice(0, 3).map((order) => (
                <Link
                  key={order.id}
                  to={`/procurement/${order.id}`}
                  className="block p-2 rounded hover:bg-muted/50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono text-sm">{order.order_number}</span>
                      {order.supplier_name && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          from {order.supplier_name}
                        </span>
                      )}
                    </div>
                    <span className="text-xs capitalize px-2 py-0.5 rounded bg-muted">
                      {order.status.replace('_', ' ')}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
